import React, { useState } from "react";

// --- ENHANCED MOCK DATA ---
const ALL_PAYMENTS = [
  {
    id: "INV-2026-001",
    title: "Royal Wedding - Venue Advance",
    date: "Jan 10, 2026",
    amount: 50000,
    status: "Paid",
    method: "Bank Transfer",
    downloadUrl: "#"
  },
  {
    id: "INV-2026-002",
    title: "Sufi Night - VIP Tickets (x2)",
    date: "Jan 28, 2026",
    amount: 3000,
    status: "Paid",
    method: "Credit Card",
    downloadUrl: "#"
  },
  {
    id: "INV-2026-003",
    title: "Corporate Gala - Catering 50%",
    date: "Feb 02, 2026",
    amount: 125000,
    status: "Pending",
    method: "Unpaid",
    dueDate: "Due in 3 days"
  },
  {
    id: "INV-2026-004",
    title: "Decor Consultation Fee",
    date: "Feb 05, 2026",
    amount: 5000,
    status: "Pending",
    method: "Unpaid",
    dueDate: "Due Tomorrow"
  }
];

const PaymentsView = () => {
  const [filter, setFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");

  // --- LOGIC: Filter & Search ---
  const filteredPayments = ALL_PAYMENTS.filter((pay) => {
    const matchesFilter = filter === "All" || pay.status === filter;
    const matchesSearch = pay.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pay.id.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const totalSpent = ALL_PAYMENTS
    .filter(p => p.status === "Paid")
    .reduce((acc, curr) => acc + curr.amount, 0);

  const pendingAmount = ALL_PAYMENTS
    .filter(p => p.status === "Pending")
    .reduce((acc, curr) => acc + curr.amount, 0);

  // Format Currency
  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumSignificantDigits: 3 }).format(amount);

  return (
    <div className="animate-fade-in-up font-sans text-[#0B1F33] pb-12">

      {/* 1. FINANCIAL SUMMARY CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">

        {/* Total Spent (Navy Card) */}
        <div className="bg-[#0B1F33] rounded-[2rem] p-8 text-white relative overflow-hidden shadow-xl shadow-[#0B1F33]/20">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-[60px] -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-full bg-white/10 backdrop-blur-md"><span className="material-symbols-outlined text-[#D4AF37]">account_balance_wallet</span></div>
              <p className="text-xs text-[#D4AF37] font-bold uppercase tracking-[0.2em]">Total Expenditure</p>
            </div>
            <h3 className="text-4xl md:text-5xl font-black mt-2">{formatCurrency(totalSpent)}</h3>
            <p className="text-xs text-gray-400 mt-2 font-medium">Across all events</p>
          </div>
        </div>

        {/* Pending Dues (White/Gold Card) */}
        <div className="bg-white border border-gray-100 rounded-[2rem] p-8 relative overflow-hidden shadow-sm">
          <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-full blur-[40px] pointer-events-none"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-full bg-red-50"><span className="material-symbols-outlined text-red-500">pending_actions</span></div>
              <p className="text-xs text-red-500 font-bold uppercase tracking-[0.2em]">Pending Dues</p>
            </div>
            <h3 className="text-4xl md:text-5xl font-black mt-2 text-[#0B1F33]">{formatCurrency(pendingAmount)}</h3>
            <button className="mt-4 text-xs font-bold text-[#0B1F33] border-b-2 border-[#D4AF37] pb-0.5 hover:text-[#D4AF37] transition-colors">
              View Breakdown
            </button>
          </div>
        </div>
      </div>

      {/* 2. TOOLBAR (Search & Filter) */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">

        {/* Filter Tabs */}
        <div className="flex bg-gray-100 p-1 rounded-full w-fit">
          {["All", "Paid", "Pending"].map((tab) => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`
                px-6 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300
                ${filter === tab ? "bg-[#0B1F33] text-white shadow-md" : "text-gray-500 hover:text-[#0B1F33]"}
              `}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Search Bar */}
        <div className="relative w-full md:w-64">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">search</span>
          <input
            type="text"
            placeholder="Search invoices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white border border-gray-200 text-sm font-bold text-[#0B1F33] placeholder:font-normal focus:outline-none focus:border-[#D4AF37] transition-colors"
          />
        </div>
      </div>

      {/* 3. PAYMENT LIST */}
      <div className="space-y-4">
        {filteredPayments.length > 0 ? (
          filteredPayments.map((pay) => (
            <div
              key={pay.id}
              className="group bg-white p-5 rounded-[1.5rem] border border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:shadow-lg hover:border-[#D4AF37]/30 transition-all duration-300"
            >
              {/* Left: Icon & Info */}
              <div className="flex items-center gap-5">
                <div className={`
                  w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 transition-colors
                  ${pay.status === "Paid" ? "bg-green-50 text-green-600 group-hover:bg-green-100" : "bg-[#FFF8E1] text-[#D4AF37] group-hover:bg-[#FFF3CD]"}
                `}>
                  <span className="material-symbols-outlined text-2xl">
                    {pay.status === "Paid" ? "receipt_long" : "hourglass_top"}
                  </span>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-[#0B1F33] mb-1">{pay.title}</h4>
                  <div className="flex items-center gap-3 text-xs text-gray-500 font-medium">
                    <span>{pay.id}</span>
                    <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                    <span>{pay.date}</span>
                    {pay.status === "Pending" && (
                      <span className="text-red-500 font-bold bg-red-50 px-2 py-0.5 rounded-md ml-1">{pay.dueDate}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Right: Amount & Action */}
              <div className="flex items-center justify-between md:justify-end gap-6 border-t md:border-t-0 border-gray-50 pt-4 md:pt-0">
                <div className="text-right">
                  <p className="text-lg font-black text-[#0B1F33]">{formatCurrency(pay.amount)}</p>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{pay.method}</p>
                </div>

                {pay.status === "Paid" ? (
                  <button className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-400 hover:bg-[#0B1F33] hover:text-white hover:border-[#0B1F33] transition-all">
                    <span className="material-symbols-outlined text-lg">download</span>
                  </button>
                ) : (
                  <button className="bg-[#D4AF37] hover:bg-[#c5a02e] text-[#0B1F33] px-6 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest shadow-lg shadow-[#D4AF37]/20 hover:-translate-y-0.5 transition-all">
                    Pay Now
                  </button>
                )}
              </div>
            </div>
          ))
        ) : (
          /* Empty State */
          <div className="text-center py-12 bg-white rounded-[2rem] border border-dashed border-gray-200">
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300">
              <span className="material-symbols-outlined text-3xl">search_off</span>
            </div>
            <p className="text-gray-500 font-bold">No invoices found</p>
            <p className="text-xs text-gray-400 mt-1">Try adjusting your filters</p>
          </div>
        )}
      </div>

    </div>
  );
};

export default PaymentsView;